import { useState, useEffect, useMemo, useCallback } from 'react';
import { LiquidationAsset } from '../coinLiquidation/types';
import { WalletTokenState, getTokensByNetwork } from '../coinLiquidation/walletTokenFetcher';
import { onGetTokensFromCustomLiquidation } from '../coinLiquidation/utils';

interface UseSelectAllProps {
  walletTokenState: WalletTokenState;
  customLiquidation: Record<string, unknown>;
  nativeLockUsd: number;
  canSelectToken: (token: LiquidationAsset) => boolean;
  setCustomLiquidation: (data: Record<string, unknown>) => void;
}

export const useSelectAll = ({
  walletTokenState,
  customLiquidation,
  nativeLockUsd,
  canSelectToken,
  setCustomLiquidation,
}: UseSelectAllProps) => {
  const [selectAll, setSelectAll] = useState(false);

  const allTokens = useMemo(() => {
    const tokens: LiquidationAsset[] = [];
    Object.keys(walletTokenState.tokenByNetwork).forEach(chainId => {
      const networkTokens = getTokensByNetwork(walletTokenState, Number(chainId))
        .filter(token => Number(token.USDBalance) > 0);
      tokens.push(...networkTokens);
    });
    return tokens;
  }, [walletTokenState]);

  const handleSelectAll = useCallback(() => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    if (newSelectAll) {
      const newCustomLiquidation: Record<string, any> = {};

      Object.keys(walletTokenState.tokenByNetwork).forEach(chainId => {
        const networkTokens = getTokensByNetwork(walletTokenState, Number(chainId))
          .filter(token => Number(token.USDBalance) > 0);

        if (networkTokens.length > 0) {
          const networkName =
            networkTokens[0]?.network?.name || chainId.toString();
          newCustomLiquidation[chainId.toString()] = {
            name: networkName,
            isSelected: true,
            ...networkTokens.reduce((acc, token) => {
              if (canSelectToken(token)) {
                acc[token.symbol] = {
                  ...token,
                  isSelected: true,
                  loading: false,
                  allocation: 0,
                  price: token.USDBalance.toString(),
                  error: '',
                };
              }
              return acc;
            }, {} as Record<string, any>)
          };
        }
      });

      setCustomLiquidation(newCustomLiquidation);
    } else {
      setCustomLiquidation({});
    }
  }, [selectAll, walletTokenState, canSelectToken, setCustomLiquidation]);

  useEffect(() => {
    const selectableTokens = allTokens.filter(canSelectToken);
    const selectedTokens = onGetTokensFromCustomLiquidation(customLiquidation);
    setSelectAll(selectableTokens.length > 0 && selectedTokens.length === selectableTokens.length);
  }, [allTokens, customLiquidation, canSelectToken]);

  return {
    selectAll,
    handleSelectAll,
  };
};
